import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Shield, User, X } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { useAppDispatch, useAppSelector } from '../../store';
import { loginUser } from '../../store/slices/authSlice';
import { lockTerminal, setTerminalSwitchMode, unlockTerminal } from '../../store/slices/uiSlice';

export default function TerminalLockScreen() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, token } = useAppSelector((state) => state.auth);
  const { terminalLockMode, terminalReturnPath } = useAppSelector((state) => state.ui);

  const isSwitchMode = terminalLockMode === 'switch';
  const [username, setUsername] = useState(isSwitchMode ? '' : user?.username || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lockedName = user ? `${user.firstName} ${user.lastName}` : 'User';

  useEffect(() => {
    if (terminalLockMode === 'switch') {
      setUsername('');
    } else if (user?.username) {
      setUsername(user.username);
    }
  }, [terminalLockMode, user?.username]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (terminalLockMode === 'lock') {
        const res = await fetch(`${API_BASE_URL}/auth/verify-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ password }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(data.error || 'Incorrect password');
          return;
        }
        dispatch(unlockTerminal());
        setPassword('');
        return;
      }

      const result = await dispatch(loginUser({ username: username.trim(), password }));
      if (!loginUser.fulfilled.match(result)) {
        setError(typeof result.payload === 'string' ? result.payload : 'Sign in failed');
        return;
      }
      dispatch(unlockTerminal());
      setPassword('');
      if (terminalReturnPath) {
        navigate(terminalReturnPath, { replace: true });
      }
    } catch {
      setError('Network error — try again');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchUser = () => {
    dispatch(setTerminalSwitchMode());
    setUsername('');
    setPassword('');
    setError(null);
  };

  const handleBackToLock = () => {
    if (!user) return;
    dispatch(lockTerminal({ returnPath: terminalReturnPath || '/', lockedUserId: user.id }));
    setUsername(user.username);
    setPassword('');
    setError(null);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(160deg, #0a0f1a 0%, #0d1525 50%, #0a0f1a 100%)' }}
    >
      <div className="w-full max-w-md glass-panel p-6 space-y-5">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/15 border border-primary/25">
            {isSwitchMode ? (
              <User className="w-7 h-7 text-primary" />
            ) : (
              <Lock className="w-7 h-7 text-primary" />
            )}
          </div>
          <h1 className="text-xl font-bold text-foreground">
            {isSwitchMode ? 'Switch user' : 'Terminal locked'}
          </h1>
          {!isSwitchMode && (
            <p className="text-sm text-muted-foreground">
              Signed in as <span className="font-semibold text-foreground">{lockedName}</span>
            </p>
          )}
          {isSwitchMode && (
            <p className="text-sm text-muted-foreground">
              Sign in to continue where you left off
            </p>
          )}
        </div>

        <form onSubmit={handleUnlock} className="space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg text-sm bg-destructive/10 border border-destructive/20 text-destructive">
              {error}
            </div>
          )}

          {isSwitchMode && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isSwitchMode ? 'current-password' : 'password'}
                required
                className="w-full px-3 py-2.5 pr-10 rounded-lg bg-background border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={cn(
              'w-full py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2',
              'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50',
            )}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {isSwitchMode ? 'Sign in' : 'Unlock terminal'}
          </button>
        </form>

        <div className="flex flex-col gap-2 pt-1 border-t border-border">
          {!isSwitchMode && (
            <button
              type="button"
              onClick={handleSwitchUser}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Switch user
            </button>
          )}
          {isSwitchMode && user && (
            <button
              type="button"
              onClick={handleBackToLock}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Back to unlock as {user.username}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
